import React, { useState } from 'react';
import { WizardData, SAMPLE_VOICES, TTS_MODELS, LLM_MODELS } from '../types';

interface FinetuningStepProps {
  data: WizardData;
  updateData: (updates: Partial<WizardData>) => void;
  onComplete: () => void;
  onPrevious: () => void;
  loading: boolean;
  error: string | null;
}

const FinetuningStep: React.FC<FinetuningStepProps> = ({ 
  data, 
  updateData, 
  onComplete, 
  onPrevious,
  loading,
  error
}) => {
  // Removed in accordance with requirement to remove test section

  const selectedVoice = SAMPLE_VOICES.find(v => v.voice_id === data.voiceId);
  const selectedTTS = TTS_MODELS.find(m => m.id === data.ttsModel);
  const selectedLLM = LLM_MODELS[data.llmProvider].find(m => m.id === data.llmModel);

  // Test handler removed

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onComplete();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-white mb-2">Fine-tune & Preview</h3>
        <p className="text-gray-400">Final adjustments before creating your agent</p>
      </div>

      {/* Configuration Summary */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-600">
        <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
          <span className="mr-2">📋</span> Configuration Summary
        </h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-400">Name:</span>
            <p className="text-white font-medium">{data.name}</p>
          </div>
          <div>
            <span className="text-gray-400">Purpose:</span>
            <p className="text-white font-medium">{data.purpose.replace('_', ' ')}</p>
          </div>
          <div>
            <span className="text-gray-400">Voice:</span>
            <p className="text-white font-medium">{selectedVoice?.name || 'Custom Voice'}</p>
          </div>
          <div>
            <span className="text-gray-400">Model:</span>
            <p className="text-white font-medium">{selectedLLM?.name}</p>
          </div>
          <div>
            <span className="text-gray-400">Language:</span>
            <p className="text-white font-medium">
              {data.primaryLanguage.toUpperCase()}
              {data.additionalLanguages.length > 0 && ` +${data.additionalLanguages.length} more`}
            </p>
          </div>
          <div>
            <span className="text-gray-400">Speech Model:</span>
            <p className="text-white font-medium">{selectedTTS?.name}</p>
          </div>
        </div>
      </div>

      {/* Fine-tuning Settings */}
      <div className="space-y-4">
        <div>
          <label htmlFor="firstMessage" className="block text-sm font-medium text-gray-300 mb-2">
            First Message
          </label>
          <textarea
            id="firstMessage"
            value={data.firstMessage}
            onChange={(e) => updateData({ firstMessage: e.target.value })}
            className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            rows={3}
            placeholder="e.g., Hello! I'm your real estate assistant. How can I help you find your dream property today?"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="turnTimeout" className="block text-sm font-medium text-gray-300 mb-2">
              Turn Timeout (seconds)
            </label>
            <input
              type="number"
              id="turnTimeout"
              value={data.turnTimeout}
              onChange={(e) => updateData({ turnTimeout: parseInt(e.target.value) })}
              min="1"
              max="30"
              className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Time to wait for user response
            </p>
          </div>

          <div>
            <label htmlFor="maxDuration" className="block text-sm font-medium text-gray-300 mb-2">
              Max Duration (seconds)
            </label>
            <input
              type="number"
              id="maxDuration"
              value={data.maxDuration}
              onChange={(e) => updateData({ maxDuration: parseInt(e.target.value) })}
              min="60"
              max="3600"
              step="60"
              className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Maximum conversation length
            </p>
          </div>
        </div>
      </div>

      {/* Test Your Agent section removed per requirements */}

      {/* Error Display */}
      {error && (
        <div className="bg-red-900/20 rounded-xl p-4 border border-red-600">
          <div className="flex items-center space-x-3">
            <span className="text-red-400 text-xl">❌</span>
            <p className="text-red-300">{error}</p>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <button
          type="button"
          onClick={onPrevious}
          className="bg-gray-700 hover:bg-gray-600 text-white font-medium px-6 py-3 rounded-xl transition-colors disabled:opacity-50"
          disabled={loading}
        >
          ← Previous
        </button>
        <button
          type="submit"
          disabled={loading}
          className="bg-green-600 hover:bg-green-700 text-white font-medium px-6 py-3 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-green-600"
        >
          {loading ? (
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              <span>Creating Agent...</span>
            </div>
          ) : (
            '🚀 Create Agent'
          )}
        </button>
      </div>


    </form>
  );
};

export default FinetuningStep;